import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import ActionCenter, { parseCalendarDate } from '../components/ActionCenter'
import OutstandingInvoicesWidget from '../components/OutstandingInvoicesWidget'

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

function formatCurrency(value) {
  return currencyFormatter.format(Number(value) || 0)
}

function formatDueDate(value) {
  if (!value) return 'No due date'
  const date = parseCalendarDate(value)
  if (!date) return 'No due date'
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function truncateDescription(value, maxLength = 40) {
  if (!value) return 'No description'
  if (value.length <= maxLength) return value
  return `${value.slice(0, maxLength - 1)}…`
}

function getDueStatus(value) {
  if (!value) return null

  const dueDate = parseCalendarDate(value)
  if (!dueDate) return null

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const dueDay = new Date(dueDate)
  dueDay.setHours(0, 0, 0, 0)

  if (dueDay < today) return 'past_due'

  const sevenDaysOut = new Date(today)
  sevenDaysOut.setDate(sevenDaysOut.getDate() + 7)
  if (dueDay <= sevenDaysOut) return 'due_soon'

  return null
}

function sortInvoicesByDueDate(invoices) {
  return [...invoices].sort((a, b) => {
    const aTime = parseCalendarDate(a.due_date)?.getTime() ?? Number.POSITIVE_INFINITY
    const bTime = parseCalendarDate(b.due_date)?.getTime() ?? Number.POSITIVE_INFINITY
    return aTime - bTime
  })
}

function StatCard({ label, value, sub, onClick }) {
  const Tag = onClick ? 'button' : 'div'
  return <Tag className="card cf-stat-card" onClick={onClick} type={onClick ? 'button' : undefined}><strong>{value}</strong><span>{label}</span><small>{sub}</small></Tag>
}

function StatusBadge({ status }) {
  const map = { not_started: ['neutral', 'Not Started'], in_progress: ['info', 'In Progress'], review: ['warning', 'In Review'], filed: ['success', 'Filed'], on_hold: ['danger', 'On Hold'] }
  const [cls, label] = map[status] || ['neutral', status]
  return <span className={`badge badge-${cls}`}>{label}</span>
}

export default function CpaDashboard() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const [clients, setClients] = useState([])
  const [recentDocs, setRecentDocs] = useState([])
  const [actionDocs, setActionDocs] = useState([])
  const [invoiceStatsLoading, setInvoiceStatsLoading] = useState(true)
  const [pendingInvoiceCount, setPendingInvoiceCount] = useState(0)
  const [pendingInvoiceOutstanding, setPendingInvoiceOutstanding] = useState(0)
  const [collectedYearCount, setCollectedYearCount] = useState(0)
  const [collectedYearTotal, setCollectedYearTotal] = useState(0)
  const [invoicesDue, setInvoicesDue] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!profile?.id) return

    async function load() {
      setInvoiceStatsLoading(true)

      const [{ data: c }, { data: d }, { data: attentionDocs }] = await Promise.all([
        supabase.from('clients').select('*').order('created_at', { ascending: false }),
        supabase.from('documents').select('*, clients(full_name, business_name)').order('created_at', { ascending: false }).limit(5),
        supabase.from('documents').select('*, clients(full_name, business_name)')
          .in('status', ['pending', 'needs_resubmission'])
          .order('created_at', { ascending: false }).limit(3)
      ])

      try {
        const currentYear = new Date().getFullYear()
        const yearStart = new Date(currentYear, 0, 1).toISOString()
        const nextYearStart = new Date(currentYear + 1, 0, 1).toISOString()

        const [{ data: pendingInvoices }, { data: paidYearInvoices }, { data: dueInvoices }] = await Promise.all([
          supabase
            .from('invoices')
            .select('total_amount')
            .eq('cpa_id', profile.id)
            .eq('status', 'sent'),
          supabase
            .from('invoices')
            .select('total_amount')
            .eq('cpa_id', profile.id)
            .eq('status', 'paid')
            .gte('paid_at', yearStart)
            .lt('paid_at', nextYearStart),
          supabase
            .from('invoices')
            .select('id, invoice_number, description, due_date, total_amount, clients(full_name, business_name)')
            .eq('cpa_id', profile.id)
            .eq('status', 'sent')
            .order('due_date', { ascending: true }),
        ])

        const pendingRows = pendingInvoices || []
        const paidRows = paidYearInvoices || []

        setPendingInvoiceCount(pendingRows.length)
        setPendingInvoiceOutstanding(pendingRows.reduce((sum, inv) => sum + (Number(inv.total_amount) || 0), 0))
        setCollectedYearCount(paidRows.length)
        setCollectedYearTotal(paidRows.reduce((sum, inv) => sum + (Number(inv.total_amount) || 0), 0))
        setInvoicesDue(sortInvoicesByDueDate(dueInvoices || []))
      } catch (_) {
        setPendingInvoiceCount(0)
        setPendingInvoiceOutstanding(0)
        setCollectedYearCount(0)
        setCollectedYearTotal(0)
        setInvoicesDue([])
      }

      setClients(c || [])
      setRecentDocs(d || [])
      setActionDocs(attentionDocs || [])
      setInvoiceStatsLoading(false)
      setLoading(false)
    }
    load()
  }, [profile?.id])

  const stats = {
    total:    clients.length,
    active:   clients.filter(c => c.status === 'in_progress').length,
    pending:  clients.filter(c => c.status === 'not_started').length,
    filed:    clients.filter(c => c.status === 'filed').length,
  }

  const invoicesDueTotal = invoicesDue.reduce((sum, inv) => sum + (Number(inv.total_amount) || 0), 0)
  const invoicesPastDueTotal = invoicesDue.reduce((sum, inv) => {
    return getDueStatus(inv.due_date) === 'past_due'
      ? sum + (Number(inv.total_amount) || 0)
      : sum
  }, 0)

  if (loading) return (
    <div className="cf-stat-grid">
      {[...Array(6)].map((_, i) => <div key={i} className="skeleton" style={{ height: 100 }} />)}
    </div>
  )

  return (
    <div className="stagger">
      <div className="cf-page-intro"><div><p className="cf-overline">YOUR FIRM AT A GLANCE</p><h2>Welcome back, {profile?.full_name?.split(' ')[0] || 'there'}.</h2><p>Here’s what needs your attention today.</p></div><button className="btn btn-primary" onClick={() => navigate('/clients')}>Open clients →</button></div>
      <ActionCenter documents={actionDocs} clients={clients} invoices={invoicesDue}/>
      {/* Stats */}
      <div className="cf-stat-grid">
        <StatCard label="Total Clients" value={stats.total} sub="This tax year" color="#CBB17B" onClick={() => navigate('/clients')} />
        <StatCard label="In Progress" value={stats.active} sub="Returns being worked" color="#3B82F6" onClick={() => navigate('/clients')} />
        <StatCard label="Not Started" value={stats.pending} sub="Need attention" color="#F59E0B" onClick={() => navigate('/clients')} />
        <StatCard label="Filed" value={stats.filed} sub="Completed returns" color="#10B981" onClick={() => navigate('/clients')} />
        <StatCard
          label="Pending Invoices"
          value={invoiceStatsLoading ? '...' : pendingInvoiceCount}
          sub={invoiceStatsLoading ? 'Loading invoice totals...' : `${formatCurrency(pendingInvoiceOutstanding)} outstanding`}
          color="#F97316"
        />
        <StatCard
          label="Collected This Year"
          value={invoiceStatsLoading ? '...' : formatCurrency(collectedYearTotal)}
          sub={invoiceStatsLoading ? 'Loading collected totals...' : `${collectedYearCount} paid invoice${collectedYearCount === 1 ? '' : 's'}`}
          color="#059669"
        />
      </div>

      <div style={{ marginBottom: 24 }}>
        <OutstandingInvoicesWidget />
      </div>

      <div className="card animate-fadeUp" style={{ marginBottom: 18 }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 4 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700 }}>Invoices Due</h3>
          <div style={{ fontSize: 12, color: '#62615D' }}>
            {`${invoicesDue.length} invoice${invoicesDue.length === 1 ? '' : 's'} · ${formatCurrency(invoicesDueTotal)} outstanding${invoicesPastDueTotal > 0 ? ` · ${formatCurrency(invoicesPastDueTotal)} past due` : ''}`}
          </div>
        </div>
        <div>
          {invoicesDue.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#62615D', fontSize: 13 }}>No unpaid invoices</div>
          ) : (
            invoicesDue.map(inv => {
              const dueStatus = getDueStatus(inv.due_date)
              const clientName = inv.clients?.business_name || inv.clients?.full_name || 'Unknown client'

              return (
                <div
                  key={inv.id}
                  role="button" tabIndex={0} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/billing', { state: { highlightInvoice: inv.id } }) } }}
                  onClick={() => navigate('/billing', { state: { highlightInvoice: inv.id } })}
                  style={{
                    padding: '12px 20px',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
                    alignItems: 'center',
                    gap: 10,
                    borderBottom: '1px solid #F7F3EB',
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#FAFAF7' }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
                >
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#202326', minWidth: 0 }}>{clientName}</div>
                  <div style={{ fontSize: 12, color: '#202326' }}>{inv.invoice_number || '—'}</div>
                  <div style={{ fontSize: 12, color: '#62615D', minWidth: 0 }}>{truncateDescription(inv.description)}</div>
                  <div style={{ fontSize: 12, color: '#202326' }}>{formatDueDate(inv.due_date)}</div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#202326' }}>{formatCurrency(inv.total_amount)}</div>
                  <div>
                    {dueStatus === 'past_due' && (
                      <span
                        style={{
                          display: 'inline-block',
                          fontSize: 10,
                          fontWeight: 700,
                          color: '#991B1B',
                          background: '#FEE2E2',
                          border: '1px solid #FCA5A5',
                          borderRadius: 999,
                          padding: '2px 8px',
                        }}
                      >
                        PAST DUE
                      </span>
                    )}
                    {dueStatus === 'due_soon' && (
                      <span
                        style={{
                          display: 'inline-block',
                          fontSize: 10,
                          fontWeight: 700,
                          color: '#92400E',
                          background: '#FEF3C7',
                          border: '1px solid #FCD34D',
                          borderRadius: 999,
                          padding: '2px 8px',
                        }}
                      >
                        DUE SOON
                      </span>
                    )}
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>

      <div className="cf-two-columns">
        {/* Client list */}
        <div className="card animate-fadeUp">
          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ fontSize: 14, fontWeight: 700 }}>All Clients</h3>
            <button className="btn btn-ghost" style={{ padding: '5px 12px', fontSize: 12 }} onClick={() => navigate('/clients')}>View All →</button>
          </div>
          <div>
            {clients.slice(0, 8).map(c => (
              <div key={c.id} role="button" tabIndex={0} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/clients/${c.id}`) } }} onClick={() => navigate(`/clients/${c.id}`)}
                style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #F7F3EB', cursor: 'pointer', transition: 'background 0.1s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#FAFAF7'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 9,
                    background: '#202326',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#CBB17B', fontWeight: 700, fontSize: 12, flexShrink: 0
                  }}>
                    {(c.business_name || c.full_name).split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{c.business_name || c.full_name}</div>
                    <div style={{ fontSize: 11, color: '#62615D' }}>{c.entity_type || 'Entity TBD'} · {c.full_name}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {c.deadline && new Date(c.deadline) < new Date(Date.now() + 7*24*60*60*1000) && (
                    <span style={{ fontSize: 10, color: '#F59E0B' }}>⚠ Due soon</span>
                  )}
                  <StatusBadge status={c.status} />
                </div>
              </div>
            ))}
            {clients.length === 0 && (
              <div style={{ padding: '32px', textAlign: 'center', color: '#62615D' }}>
                <div style={{ fontSize: 32, marginBottom: 10 }}>👥</div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>No clients yet</div>
                <button className="btn btn-primary" style={{ marginTop: 8 }} onClick={() => navigate('/clients')}>Add Your First Client</button>
              </div>
            )}
          </div>
        </div>

        {/* Right column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Recent docs */}
          <div className="card animate-fadeUp">
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ fontSize: 14, fontWeight: 700 }}>Recent Documents</h3>
              <button className="btn btn-ghost" style={{ padding: '5px 12px', fontSize: 12 }} onClick={() => navigate('/documents')}>View All →</button>
            </div>
            {recentDocs.length === 0
              ? <div style={{ padding: '20px', textAlign: 'center', color: '#62615D', fontSize: 13 }}>No documents yet</div>
              : recentDocs.map(d => (
              <div key={d.id} style={{ padding: '10px 18px', borderBottom: '1px solid #F7F3EB', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                  <span style={{ fontSize: 18 }}>{d.file_name?.endsWith('.pdf') ? '📄' : d.file_name?.endsWith('.xlsx') ? '📊' : '📁'}</span>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 500 }}>{d.file_name}</div>
                    <div style={{ fontSize: 11, color: '#62615D' }}>{d.clients?.business_name || d.clients?.full_name}</div>
                  </div>
                </div>
                <span className={`badge badge-${d.status === 'reviewed' ? 'success' : d.status === 'needs_resubmission' ? 'danger' : 'warning'}`} style={{ fontSize: 10 }}>
                  {d.status}
                </span>
              </div>
            ))}
          </div>

          {/* Quick actions */}
          <div className="card animate-fadeUp" style={{ padding: '18px' }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 14 }}>Quick Actions</h3>
            <div style={{ display: 'grid', gap: 8 }}>
              {[
                { label: 'Manage clients', path: '/clients', icon: '👤' },
                { label: 'Review Documents', path: '/documents', icon: '📋' },
                { label: 'Send Reminders', path: '/reminders', icon: '🔔' },
                { label: 'Manage invoices', path: '/invoices', icon: '🧾' },
              ].map(a => (
                <button key={a.path} className="btn btn-ghost" style={{ justifyContent: 'flex-start', width: '100%' }} onClick={() => navigate(a.path)}>
                  <span>{a.icon}</span> {a.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
